// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {useTooltipPortalRoot} from '@app/features/ui/tooltip/Tooltip';
import {appZoomLayoutPx} from '@app/features/ui/utils/AppZoomUtils';
import styles from '@app/features/voice/components/media_player/MediaProgressBar.module.css';
import {
	clampPercentage,
	getSeekPercentageFromClientX,
} from '@app/features/voice/components/media_player/utils/MediaSeekUtils';
import {FloatingPortal} from '@floating-ui/react';
import {formatDuration} from '@fluxer/date_utils/src/DateDuration';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {AnimatePresence, motion} from 'framer-motion';
import type React from 'react';
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

const MEDIA_PROGRESS_DESCRIPTOR = msg({
	message: 'Media progress',
	comment: 'Aria label on the media player progress / scrubber slider.',
});
const TOOLTIP_OFFSET_PX = 8;
const TOOLTIP_VIEWPORT_PADDING_PX = 8;

export interface MediaProgressBarProps {
	progress: number;
	buffered?: number;
	currentTime?: number;
	duration?: number;
	isSeeking?: boolean;
	onSeek?: (percentage: number) => void;
	onSeekPreview?: (percentage: number) => void;
	onSeekStart?: () => void;
	onSeekEnd?: () => void;
	showPreview?: boolean;
	previewThumbnail?: React.ReactNode;
	previewThumbnailWidth?: number;
	previewPortalRoot?: HTMLElement | null;
	className?: string;
	compact?: boolean;
	ariaLabel?: string;
}

export function MediaProgressBar({
	progress,
	buffered = 0,
	currentTime = 0,
	duration = 0,
	onSeek,
	onSeekPreview,
	onSeekStart,
	onSeekEnd,
	showPreview = true,
	previewThumbnail,
	previewThumbnailWidth,
	previewPortalRoot,
	className,
	compact = false,
	ariaLabel,
}: MediaProgressBarProps) {
	const {i18n} = useLingui();
	const containerRef = useRef<HTMLDivElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const globalTooltipPortalRoot = useTooltipPortalRoot(previewPortalRoot == null);
	const tooltipPortalRoot = previewPortalRoot ?? globalTooltipPortalRoot;
	const [isDragging, setIsDragging] = useState(false);
	const [hoverPosition, setHoverPosition] = useState<number | null>(null);
	const [hoverTime, setHoverTime] = useState<string>('0:00');
	const [tooltipPosition, setTooltipPosition] = useState({x: 0, y: 0, isReady: false});
	const tooltipThumbnailStyle =
		previewThumbnailWidth != null
			? ({'--media-progress-tooltip-thumbnail-width': `${previewThumbnailWidth}px`} as React.CSSProperties)
			: undefined;
	const rafRef = useRef<number | null>(null);
	const cachedRectRef = useRef<DOMRect | null>(null);
	const pendingSeekRef = useRef<number | null>(null);
	const activePointerIdRef = useRef<number | null>(null);
	const lastSeekPercentageRef = useRef(clampPercentage(progress));
	const cacheContainerRect = useCallback(() => {
		const container = containerRef.current;
		if (!container) return null;
		const rect = container.getBoundingClientRect();
		cachedRectRef.current = rect;
		return rect;
	}, []);
	const clearCachedRect = useCallback(() => {
		cachedRectRef.current = null;
	}, []);
	const getPercentageFromEvent = useCallback(
		(clientX: number): number => {
			const container = containerRef.current;
			if (!container) return 0;
			const rect = cachedRectRef.current ?? cacheContainerRect();
			if (!rect) return 0;
			return getSeekPercentageFromClientX(clientX, rect);
		},
		[cacheContainerRect],
	);
	const setHoverPositionFromPercentage = useCallback(
		(percentage: number) => {
			const clampedPercentage = clampPercentage(percentage);
			setHoverPosition(clampedPercentage);
			if (duration > 0) {
				setHoverTime(formatDuration((clampedPercentage / 100) * duration));
			} else {
				setHoverTime(formatDuration(0));
			}
		},
		[duration],
	);
	const scheduleSeekPreview = useCallback(
		(percentage: number) => {
			if (!onSeekPreview) return;
			pendingSeekRef.current = percentage;
			if (rafRef.current !== null) return;
			rafRef.current = requestAnimationFrame(() => {
				const nextPercentage = pendingSeekRef.current;
				pendingSeekRef.current = null;
				rafRef.current = null;
				if (nextPercentage !== null) {
					onSeekPreview(nextPercentage);
				}
			});
		},
		[onSeekPreview],
	);
	const flushSeekPreview = useCallback(() => {
		if (rafRef.current !== null) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
		const nextPercentage = pendingSeekRef.current;
		pendingSeekRef.current = null;
		if (nextPercentage !== null) {
			onSeekPreview?.(nextPercentage);
		}
	}, [onSeekPreview]);
	const previewSeek = useCallback(
		(percentage: number) => {
			const clampedPercentage = clampPercentage(percentage);
			lastSeekPercentageRef.current = clampedPercentage;
			setHoverPositionFromPercentage(clampedPercentage);
			scheduleSeekPreview(clampedPercentage);
		},
		[scheduleSeekPreview, setHoverPositionFromPercentage],
	);
	const updateTooltipPosition = useCallback(() => {
		if (hoverPosition === null || !containerRef.current || !tooltipRef.current) {
			return;
		}
		const container = containerRef.current;
		const tooltip = tooltipRef.current;
		const rect = cachedRectRef.current ?? container.getBoundingClientRect();
		const tooltipRect = tooltip.getBoundingClientRect();
		const tooltipWidth = tooltipRect.width || tooltip.offsetWidth;
		const tooltipHeight = tooltipRect.height || tooltip.offsetHeight;
		const anchorX = rect.left + (hoverPosition / 100) * rect.width;
		const minX = TOOLTIP_VIEWPORT_PADDING_PX;
		const maxX = Math.max(minX, window.innerWidth - tooltipWidth - TOOLTIP_VIEWPORT_PADDING_PX);
		const nextX = Math.max(minX, Math.min(maxX, anchorX - tooltipWidth / 2));
		const aboveY = rect.top - tooltipHeight - TOOLTIP_OFFSET_PX;
		const belowY = rect.bottom + TOOLTIP_OFFSET_PX;
		const unclampedY = aboveY >= TOOLTIP_VIEWPORT_PADDING_PX ? aboveY : belowY;
		const maxY = Math.max(
			TOOLTIP_VIEWPORT_PADDING_PX,
			window.innerHeight - tooltipHeight - TOOLTIP_VIEWPORT_PADDING_PX,
		);
		const nextY = Math.max(TOOLTIP_VIEWPORT_PADDING_PX, Math.min(maxY, unclampedY));
		setTooltipPosition((previousPosition) => {
			const x = appZoomLayoutPx(nextX);
			const y = appZoomLayoutPx(nextY);
			if (previousPosition.isReady && previousPosition.x === x && previousPosition.y === y) {
				return previousPosition;
			}
			return {x, y, isReady: true};
		});
	}, [hoverPosition]);
	useLayoutEffect(() => {
		if (hoverPosition !== null && tooltipRef.current) {
			updateTooltipPosition();
		}
	}, [hoverPosition, previewThumbnail, previewThumbnailWidth, tooltipPortalRoot, updateTooltipPosition]);
	useEffect(() => {
		return () => {
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			pendingSeekRef.current = null;
		};
	}, []);
	const handlePointerMove = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			if (activePointerIdRef.current === e.pointerId) {
				e.preventDefault();
				previewSeek(getPercentageFromEvent(e.clientX));
				return;
			}
			if (e.pointerType !== 'touch') {
				setHoverPositionFromPercentage(getPercentageFromEvent(e.clientX));
			}
		},
		[getPercentageFromEvent, previewSeek, setHoverPositionFromPercentage],
	);
	const handlePointerDown = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			if (e.pointerType === 'mouse' && e.button !== 0) return;
			e.preventDefault();
			cacheContainerRect();
			activePointerIdRef.current = e.pointerId;
			e.currentTarget.setPointerCapture?.(e.pointerId);
			setIsDragging(true);
			onSeekStart?.();
			previewSeek(getPercentageFromEvent(e.clientX));
			flushSeekPreview();
		},
		[cacheContainerRect, flushSeekPreview, getPercentageFromEvent, onSeekStart, previewSeek],
	);
	const finishPointerSeek = useCallback(
		(e: React.PointerEvent<HTMLDivElement>, commit: boolean) => {
			if (activePointerIdRef.current !== e.pointerId) return;
			e.preventDefault();
			const percentage = getPercentageFromEvent(e.clientX);
			previewSeek(percentage);
			flushSeekPreview();
			if (commit) {
				onSeek?.(lastSeekPercentageRef.current);
			}
			activePointerIdRef.current = null;
			e.currentTarget.releasePointerCapture?.(e.pointerId);
			setIsDragging(false);
			if (e.pointerType === 'touch') {
				setHoverPosition(null);
				setTooltipPosition((prev) => ({...prev, isReady: false}));
			}
			clearCachedRect();
			onSeekEnd?.();
		},
		[clearCachedRect, flushSeekPreview, getPercentageFromEvent, onSeek, onSeekEnd, previewSeek],
	);
	const handlePointerLeave = useCallback(() => {
		if (!isDragging) {
			setHoverPosition(null);
			setTooltipPosition((prev) => ({...prev, isReady: false}));
			clearCachedRect();
		}
	}, [clearCachedRect, isDragging]);
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (!duration) return;
			let newPercentage = clampPercentage(progress);
			const step = 5;
			switch (e.key) {
				case 'ArrowLeft':
					e.preventDefault();
					newPercentage = clampPercentage(progress - step);
					break;
				case 'ArrowRight':
					e.preventDefault();
					newPercentage = clampPercentage(progress + step);
					break;
				case 'Home':
					e.preventDefault();
					newPercentage = 0;
					break;
				case 'End':
					e.preventDefault();
					newPercentage = 100;
					break;
				default:
					return;
			}
			onSeekPreview?.(newPercentage);
			onSeek?.(newPercentage);
		},
		[progress, duration, onSeek, onSeekPreview],
	);
	const displayProgress = clampPercentage(isDragging && hoverPosition !== null ? hoverPosition : progress);
	const displayCurrentTime = duration > 0 ? (displayProgress / 100) * duration : currentTime;
	return (
		<div
			ref={containerRef}
			className={clsx(styles.container, compact && styles.compact, isDragging && styles.isDragging, className)}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={(e) => finishPointerSeek(e, true)}
			onPointerCancel={(e) => finishPointerSeek(e, true)}
			onPointerLeave={handlePointerLeave}
			onKeyDown={handleKeyDown}
			role="slider"
			aria-label={ariaLabel || i18n._(MEDIA_PROGRESS_DESCRIPTOR)}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={Math.round(displayProgress)}
			aria-valuetext={formatDuration(displayCurrentTime)}
			tabIndex={0}
			data-flx="voice.media-player.media-progress-bar.container.key-down"
		>
			<div className={styles.track} data-flx="voice.media-player.media-progress-bar.track">
				<div
					className={styles.buffered}
					style={{width: `${buffered}%`}}
					data-flx="voice.media-player.media-progress-bar.buffered"
				/>
				<div
					className={styles.fill}
					style={{width: `${displayProgress}%`}}
					data-flx="voice.media-player.media-progress-bar.fill"
				/>
			</div>
			<div
				className={styles.thumb}
				style={{left: `${displayProgress}%`}}
				data-flx="voice.media-player.media-progress-bar.thumb"
			/>
			{showPreview && hoverPosition !== null && (
				<FloatingPortal root={tooltipPortalRoot} data-flx="voice.media-player.media-progress-bar.floating-portal">
					<AnimatePresence mode="wait" data-flx="voice.media-player.media-progress-bar.animate-presence">
						<motion.div
							key="progress-tooltip"
							ref={tooltipRef}
							className={styles.tooltip}
							style={{
								position: 'fixed',
								left: tooltipPosition.x,
								top: tooltipPosition.y,
								visibility: tooltipPosition.isReady ? 'visible' : 'hidden',
							}}
							initial={{opacity: Accessibility.useReducedMotion ? 1 : 0}}
							animate={{opacity: 1}}
							exit={{opacity: Accessibility.useReducedMotion ? 1 : 0}}
							transition={{
								opacity: {duration: Accessibility.useReducedMotion ? 0 : 0.1},
							}}
							data-flx="voice.media-player.media-progress-bar.tooltip"
						>
							{previewThumbnail && (
								<div
									className={styles.tooltipThumbnail}
									style={tooltipThumbnailStyle}
									data-flx="voice.media-player.media-progress-bar.tooltip-thumbnail"
								>
									{previewThumbnail}
								</div>
							)}
							<div className={styles.tooltipTime} data-flx="voice.media-player.media-progress-bar.tooltip-time">
								{hoverTime}
							</div>
							<div className={styles.tooltipArrow} data-flx="voice.media-player.media-progress-bar.tooltip-arrow" />
						</motion.div>
					</AnimatePresence>
				</FloatingPortal>
			)}
		</div>
	);
}
